import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Briefcase,
  CalendarCheck,
  CircleDollarSign,
  Mail,
  MessageSquareQuote,
  Users,
} from "lucide-react";
import { adminApi, formatAdminError } from "@/lib/adminApi";
import {
  EmptyState,
  PageHeader,
  Panel,
  StatPill,
  StatusBadge,
  adminCurrency,
  relativeTime,
  shortDate,
  weekdayFromDateOnly,
} from "@/components/admin/uiKit";
import { OwnerEventsPanel } from "@/components/admin/OwnerEventsPanel";

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminApi.get("/admin/overview"),
      adminApi.get("/admin/jobs", { params: { status: "scheduled", limit: 6 } }),
    ])
      .then(([ov, j]) => {
        if (cancelled) return;
        setData(ov.data);
        setJobs(j.data.items || []);
      })
      .catch((err) => !cancelled && setError(formatAdminError(err)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div data-testid="admin-overview-error">
        <PageHeader overline="Overview" title="Dashboard" />
        <Panel className="p-6">
          <p className="text-sm text-rose-600">Could not load overview: {error}</p>
        </Panel>
      </div>
    );
  }

  if (!data) {
    return (
      <div data-testid="admin-overview-loading">
        <PageHeader overline="Overview" title="Dashboard" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-28 rounded-xl bg-white border border-slate-200 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const k = data.kpis;

  return (
    <div data-testid="admin-overview">
      <PageHeader
        overline="Overview"
        title="Welcome back, Isaac."
        description="At-a-glance view of customers, recurring revenue, and what's coming up this week."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
        <StatPill
          label="Active customers"
          value={k.customers_active}
          hint={`${k.customers_total} total · ${k.customers_canceled} canceled`}
          icon={Users}
          accent="sky"
        />
        <StatPill
          label="Monthly recurring"
          value={adminCurrency(k.mrr_cents)}
          hint="Sum of active subs"
          icon={CircleDollarSign}
          accent="emerald"
        />
        <StatPill
          label="Jobs this week"
          value={k.jobs_this_week}
          hint={`${k.jobs_completed_total} completed all-time`}
          icon={Briefcase}
          accent="gold"
        />
        <StatPill
          label="Pending quotes"
          value={k.pending_quotes}
          hint={`${k.quotes_total} requests received`}
          icon={MessageSquareQuote}
          accent="rose"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Panel className="lg:col-span-2 p-6" testId="admin-overview-jobs-panel">
          <header className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                Coming up
              </p>
              <h2
                className="text-xl font-bold text-slate-900"
                style={{ fontFamily: "Outfit, ui-sans-serif" }}
              >
                Next 6 scheduled cleanings
              </h2>
            </div>
            <Link
              to="/admin/jobs"
              className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700 hover:text-sky-600"
              data-testid="admin-overview-view-all-jobs"
            >
              All jobs <ArrowUpRight size={14} />
            </Link>
          </header>
          {jobs.length === 0 ? (
            <EmptyState
              title="No upcoming jobs yet"
              description="When a customer signs up, their next 4 cleanings will land here automatically."
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {jobs.map((j) => (
                <li
                  key={j.id}
                  className="flex items-center justify-between py-3 gap-4"
                  data-testid={`admin-overview-job-${j.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-sky-50 text-sky-700 grid place-items-center shrink-0">
                      <CalendarCheck size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {shortDate(j.scheduled_for)} · {weekdayFromDateOnly(j.scheduled_for) || j.service_day}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {j.customer_name || j.customer_email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <StatusBadge status={j.status} />
                    <Link
                      to={`/admin/jobs?focus=${j.id}`}
                      className="text-xs font-bold text-sky-700 hover:text-sky-500"
                    >
                      Open →
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel className="p-6" testId="admin-overview-emails-panel">
          <header className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                Recent activity
              </p>
              <h2
                className="text-xl font-bold text-slate-900"
                style={{ fontFamily: "Outfit, ui-sans-serif" }}
              >
                Last 5 emails
              </h2>
            </div>
            <Link
              to="/admin/email-log"
              className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700 hover:text-sky-600"
              data-testid="admin-overview-view-all-emails"
            >
              <Mail size={14} />
            </Link>
          </header>
          {(!data.recent_emails || data.recent_emails.length === 0) ? (
            <EmptyState
              title="No emails yet"
              description="When the system sends a welcome, completion, or quote email it will land here."
            />
          ) : (
            <ul className="space-y-3">
              {data.recent_emails.map((e) => (
                <li
                  key={e.id}
                  className="rounded-lg border border-slate-100 p-3 hover:border-slate-200 transition-colors"
                  data-testid={`admin-overview-email-${e.id}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <p className="text-xs font-bold text-slate-700 uppercase tracking-[0.16em]">
                      {(e.template || "").replace(/_/g, " ")}
                    </p>
                    <StatusBadge status={e.status} />
                  </div>
                  <p className="text-sm text-slate-900 truncate">
                    {e.subject}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    To {e.to_email} · {relativeTime(e.created_at)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <OwnerEventsPanel events={data.recent_owner_events} />
      </div>
    </div>
  );
}
